// src/pages/DriverStatsPage.js — per-driver activity summary.
// Backed by GET /driver/:id/stats — see backend/routes/driver_mgmt.js.

import React, { useCallback, useEffect, useState } from "react";
import { api } from "../scripts/api";
import {
  BarChart3, RefreshCw, Truck, Clock, Receipt, MapPin,
} from "lucide-react";

const CATEGORY_COLORS = {
  fuel: "#f59e0b", meal: "#ef4444", hotel: "#8b5cf6", tolls: "#3b82f6",
  repair: "#dc2626", parking: "#0891b2", other: "#6b7280",
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
function ninetyDaysAgoISO() { return new Date(Date.now() - 90*24*60*60*1000).toISOString().slice(0, 10); }

function fmtHours(h) {
  if (!Number.isFinite(h) || h <= 0) return "0 h";
  if (h < 1) return `${Math.round(h * 60)} min`;
  const whole = Math.floor(h);
  const mins  = Math.round((h - whole) * 60);
  return mins ? `${whole}h ${mins}m` : `${whole}h`;
}

export default function DriverStatsPage() {
  const [drivers,  setDrivers]  = useState([]);
  const [driverId, setDriverId] = useState("");
  const [from,     setFrom]     = useState(() => ninetyDaysAgoISO());
  const [to,       setTo]       = useState(() => todayISO());
  const [stats,    setStats]    = useState(null);
  const [team,     setTeam]     = useState(null);
  const [mode,     setMode]     = useState("individual"); // 'individual' | 'team'
  const [loading,  setLoading]  = useState(false);
  const [msg,      setMsg]      = useState("");
  const [recalcBusy, setRecalcBusy] = useState(null);

  const loadDrivers = useCallback(async () => {
    try {
      const { data } = await api.get("/driver");
      const list = Array.isArray(data) ? data : [];
      setDrivers(list);
      if (!driverId && list.length) setDriverId(String(list[0].DriverId));
    } catch (_) {}
  }, [driverId]);

  const load = useCallback(async () => {
    setLoading(true); setMsg("");
    try {
      if (mode === 'team') {
        const { data } = await api.get(`/driver/stats/team`, { params: { from, to } });
        setTeam(data);
      } else {
        if (!driverId) { setStats(null); return; }
        const { data } = await api.get(`/driver/${driverId}/stats`, { params: { from, to } });
        setStats(data);
      }
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load stats");
      if (mode === 'team') setTeam(null); else setStats(null);
    } finally { setLoading(false); }
  }, [mode, driverId, from, to]);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);
  useEffect(() => { load(); }, [load]);

  // When the backend returns 400 with `needs: {origin, destination}`, prompt
  // the user for the missing side(s), then retry. The first attempt is made
  // with no body so the server uses whatever it has cached / can infer.
  const recalcDistance = async (tripId, extra = {}) => {
    setRecalcBusy(tripId); setMsg("");
    try {
      const r = await api.post(`/moves/${tripId}/recalc-distance`, extra);
      const d = r.data;
      const resolved = (d.origin_resolved && d.destination_resolved)
        ? `\nResolved: "${d.origin_resolved}" → "${d.destination_resolved}"`
        : '';
      setMsg(`✓ Move #${tripId}: ${d.distance_text} (${d.duration_text})${resolved}`);
      await load();
    } catch (e) {
      const data = e?.response?.data;
      if (e?.response?.status === 400 && data?.needs) {
        const have   = data.have || {};
        const body   = { ...extra };
        const promptHelp = "Include the city AND province/state so Google doesn't match a local street\n" +
                           'Good: "Montreal, QC" or "1234 Main St, Edmonton, AB"\n' +
                           'Bad:  "Montreal"';
        if (data.needs.origin) {
          const v = window.prompt(
            `Move #${tripId} needs an ORIGIN address.\n\n${promptHelp}`,
            have.origin || ""
          );
          if (!v) { setMsg(`Move #${tripId}: cancelled.`); setRecalcBusy(null); return; }
          body.origin = v;
        }
        if (data.needs.destination) {
          const v = window.prompt(
            `Move #${tripId} needs a DESTINATION address.\n\n${promptHelp}`,
            have.destination || ""
          );
          if (!v) { setMsg(`Move #${tripId}: cancelled.`); setRecalcBusy(null); return; }
          body.destination = v;
        }
        setRecalcBusy(null);
        return recalcDistance(tripId, body);
      }
      const err = data?.error || "Recalc failed";
      // When Google returned resolved addresses but couldn't route between
      // them, show what it matched — that's how you spot "San Luis" →
      // "San Luis Province, Argentina" and know to retype with more context.
      const resolvedLine = (data?.origin_resolved || data?.destination_resolved)
        ? `\nGoogle saw: "${data.origin_resolved || data.origin_sent || '?'}" → "${data.destination_resolved || data.destination_sent || '?'}"`
        : '';
      setMsg(`Move #${tripId}: ${err}${resolvedLine}`);
    } finally { setRecalcBusy(null); }
  };

  const categoryTotals = stats?.expenses?.by_category || {};
  const maxCat = Math.max(1, ...Object.values(categoryTotals));

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>
            <BarChart3 size={22} style={{ verticalAlign: -4, marginRight: 8 }} />
            Driver Stats
          </h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Moves, mileage, hours, and expenses per driver over a date range.
          </div>
        </div>
        <button className="btn-outline" onClick={load} disabled={loading}>
          <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
          Refresh
        </button>
      </div>

      {/* Controls */}
      <div style={{
        marginTop: 14, padding: 10, background: "var(--card-bg)",
        border: "1px solid var(--card-border)", borderRadius: 12,
        display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
      }}>
        {/* Mode toggle */}
        <div style={{ display: "flex", border: "1px solid var(--card-border)", borderRadius: 8, overflow: "hidden" }}>
          {[{ k: "individual", label: "Individual" }, { k: "team", label: "Team Totals" }].map(o => (
            <button
              key={o.k}
              onClick={() => setMode(o.k)}
              style={{
                padding: "8px 14px", fontSize: 13, fontWeight: 800,
                background: mode === o.k ? "#3b82f6" : "transparent",
                color:      mode === o.k ? "#fff"    : "var(--fg)",
                border: "none", cursor: "pointer",
              }}
            >{o.label}</button>
          ))}
        </div>
        {mode === "individual" && (
          <select className="input" value={driverId} onChange={e => setDriverId(e.target.value)} style={{ flex: "1 1 180px", maxWidth: 280 }}>
            {drivers.map(d => (
              <option key={d.DriverId} value={d.DriverId}>
                {d.FullName || `#${d.DriverId}`}{d.home_terminal ? ` · ${d.home_terminal}` : ""}
              </option>
            ))}
          </select>
        )}
        <div style={{ display: "flex", gap: 4, alignItems: "center", marginLeft: "auto" }}>
          <input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ maxWidth: 150 }} />
          <span className="muted">→</span>
          <input className="input" type="date" value={to} onChange={e => setTo(e.target.value)} style={{ maxWidth: 150 }} />
        </div>
      </div>

      {msg && <div className="card" style={{ marginTop: 10, fontSize: 13, whiteSpace: "pre-wrap" }}>{msg}</div>}

      {loading && <div className="muted" style={{ marginTop: 14 }}>Loading…</div>}

      {mode === "team" && team && (
        <>
          <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <StatCard icon={Truck}  label="Moves Completed" value={team.totals.moves_completed} sub={`${team.totals.moves_in_progress} in progress · ${team.totals.moves_total} total`} color="#2563eb" />
            <StatCard icon={MapPin} label="Total Distance"   value={`${team.totals.distance_km.toLocaleString()} km`} sub="sum of all moves" color="#059669" />
            <StatCard icon={Clock}  label="Total Hours"      value={fmtHours(team.totals.hours)} sub="completed moves only" color="#7c3aed" />
            <StatCard icon={Truck}  label="Units Moved"      value={team.totals.units_moved} sub="distinct inventory IDs" color="#ea580c" />
            <StatCard icon={Receipt} label="Total Expenses" value={`$${team.totals.expense_total.toFixed(2)}`} sub={`${team.totals.expense_count} receipts`} color="#9333ea" />
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Moves by Type</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <PillCount label="Factory → SNA"   value={team.totals.by_type.factory_to_sna}  color="#2563eb" />
              <PillCount label="SNA → Customer"  value={team.totals.by_type.sna_to_customer} color="#16a34a" />
            </div>
          </div>

          <div className="card" style={{ marginTop: 12, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 14px", fontWeight: 800, borderBottom: "1px solid var(--card-border)" }}>
              Driver Leaderboard
            </div>
            {team.leaderboard.length === 0 ? (
              <div className="muted" style={{ padding: 14 }}>No driver activity in this period.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "rgba(127,127,127,0.06)" }}>
                      <Th>#</Th><Th>Driver</Th><Th>Terminal</Th>
                      <Th style={{ textAlign: "right" }}>Moves</Th>
                      <Th style={{ textAlign: "right" }}>Units</Th>
                      <Th style={{ textAlign: "right" }}>Distance</Th>
                      <Th style={{ textAlign: "right" }}>Hours</Th>
                      <Th style={{ textAlign: "right" }}>Expenses</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {team.leaderboard.map((d, i) => (
                      <tr key={d.driver_id} style={{ borderTop: "1px solid var(--card-border)", background: i < 3 ? "rgba(59,130,246,0.04)" : undefined }}>
                        <Td style={{ fontWeight: 800, width: 40 }}>
                          {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                        </Td>
                        <Td style={{ fontWeight: 700 }}>{d.name}</Td>
                        <Td className="muted">{d.home_terminal || "—"}</Td>
                        <Td style={{ textAlign: "right" }}>
                          {d.moves_completed}
                          <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>/ {d.moves_total}</span>
                        </Td>
                        <Td style={{ textAlign: "right" }}>{d.units_moved}</Td>
                        <Td style={{ textAlign: "right", fontWeight: 700 }}>{d.distance_km.toLocaleString()} km</Td>
                        <Td style={{ textAlign: "right" }}>{fmtHours(d.hours)}</Td>
                        <Td style={{ textAlign: "right" }}>${d.expense_total.toFixed(2)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="muted" style={{ padding: 10, fontSize: 11, borderTop: "1px solid var(--card-border)" }}>
              Moves column: completed / total. Sorted by distance descending.
            </div>
          </div>
        </>
      )}

      {mode === "individual" && stats && (
        <>
          {/* Summary cards */}
          <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <StatCard icon={Truck}  label="Moves completed" value={stats.moves.completed} sub={`${stats.moves.in_progress} in progress`} color="#2563eb" />
            <StatCard icon={MapPin} label="Distance"         value={`${stats.distance_km_total.toLocaleString()} km`} sub="sum of move.distance_km" color="#059669" />
            <StatCard icon={Clock}  label="Hours driven"     value={fmtHours(stats.hours_total)}                       sub="completed moves only" color="#7c3aed" />
            <StatCard icon={Receipt} label="Expenses"        value={`$${stats.expenses.total_amount.toFixed(2)}`}       sub={`${stats.expenses.count} receipts · ${stats.expenses.pending_hardcopy} pending`} color="#9333ea" />
          </div>

          {/* Move type breakdown */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Moves by Type</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <PillCount label="Factory → SNA"   value={stats.moves.by_type.factory_to_sna}  color="#2563eb" />
              <PillCount label="SNA → Customer"  value={stats.moves.by_type.sna_to_customer} color="#16a34a" />
            </div>
          </div>

          {/* Expenses by category */}
          {Object.keys(categoryTotals).length > 0 && (
            <div className="card" style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 800, marginBottom: 10 }}>Expenses by Category</div>
              <div style={{ display: "grid", gap: 6 }}>
                {Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
                  const pct = Math.round((amt / maxCat) * 100);
                  const col = CATEGORY_COLORS[cat] || "#6b7280";
                  return (
                    <div key={cat} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 80, fontSize: 13, fontWeight: 700, textTransform: "capitalize" }}>{cat}</div>
                      <div style={{ flex: 1, background: "rgba(127,127,127,0.08)", borderRadius: 6, overflow: "hidden", height: 18 }}>
                        <div style={{ width: `${pct}%`, background: col, height: "100%" }} />
                      </div>
                      <div style={{ width: 100, textAlign: "right", fontWeight: 800, fontSize: 13 }}>${amt.toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent moves table */}
          <div className="card" style={{ marginTop: 12, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 14px", fontWeight: 800, borderBottom: "1px solid var(--card-border)" }}>
              Recent Moves ({stats.recent_moves.length})
            </div>
            {stats.recent_moves.length === 0 ? (
              <div className="muted" style={{ padding: 14 }}>No moves in this period.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "rgba(127,127,127,0.06)" }}>
                      <Th>Move</Th><Th>Type</Th><Th>Unit</Th><Th>Destination</Th><Th>Start</Th><Th>Completed</Th>
                      <Th style={{ textAlign: "right" }}>Distance</Th>
                      <Th style={{ textAlign: "right" }}>Hours</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_moves.map(m => {
                      const h = (m.start_date && m.completed_at)
                        ? (new Date(m.completed_at).getTime() - new Date(m.start_date).getTime()) / 3600000
                        : null;
                      return (
                        <tr key={m.trip_id} style={{ borderTop: "1px solid var(--card-border)" }}>
                          <Td>#{m.trip_id}</Td>
                          <Td>
                            {m.move_type === "sna_to_customer"
                              ? <Pill bg="#dcfce7" color="#166534">SNA → Cust</Pill>
                              : <Pill bg="#dbeafe" color="#1e40af">Factory → SNA</Pill>}
                          </Td>
                          <Td>{m.unit_rowo || <span className="muted">—</span>}</Td>
                          <Td>{m.destination || <span className="muted">—</span>}{m.customer ? <div className="muted" style={{ fontSize: 11 }}>{m.customer}</div> : null}</Td>
                          <Td className="muted">{m.start_date || "—"}</Td>
                          <Td className="muted">{m.completed_at ? m.completed_at.slice(0, 10) : <Pill bg="#fef3c7" color="#92400e">active</Pill>}</Td>
                          <Td style={{ textAlign: "right", fontWeight: 700 }}>
                            {m.distance_km > 0 ? `${m.distance_km} km` : <span className="muted">—</span>}
                          </Td>
                          <Td style={{ textAlign: "right" }}>{h != null ? fmtHours(h) : <span className="muted">—</span>}</Td>
                          <Td>
                            <button
                              className="btn-outline"
                              style={{ padding: "4px 8px", fontSize: 11 }}
                              onClick={() => recalcDistance(m.trip_id)}
                              disabled={recalcBusy === m.trip_id}
                              title="Recompute via Google Distance Matrix"
                            >
                              {recalcBusy === m.trip_id ? "…" : "Recalc"}
                            </button>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="muted" style={{ padding: 10, fontSize: 11, borderTop: "1px solid var(--card-border)" }}>
              Recalc uses Google Distance Matrix. Requires <code>route_origin</code> / <code>route_destination</code> on the move, or <code>destination</code> as a fallback.
              Set these on the Drive-Away edit form if Recalc returns "origin/destination required."
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="card" style={{ flex: "1 1 180px", minWidth: 160, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, opacity: 0.6 }}>
        <Icon size={14} />
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 900, color, marginTop: 6 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function PillCount({ label, value, color }) {
  return (
    <div style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${color}44`, background: color + "12", display: "flex", alignItems: "baseline", gap: 8 }}>
      <span style={{ fontSize: 22, fontWeight: 900, color }}>{value}</span>
      <span style={{ fontSize: 12, opacity: 0.7, fontWeight: 700 }}>{label}</span>
    </div>
  );
}
function Pill({ bg, color, children }) {
  return <span style={{ background: bg, color, padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 700 }}>{children}</span>;
}
const Th = ({ children, style }) => (
  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, opacity: 0.6, ...style }}>
    {children}
  </th>
);
const Td = ({ children, style, className }) => (
  <td className={className} style={{ padding: "10px 12px", ...style }}>{children}</td>
);
